import { captureException } from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { LoginCard } from "@/components/auth/login-card";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { findInstanceSignInAddress, findSignInAddress } from "@/lib/api";
import {
	clientOf,
	type Destination,
	parseDestination,
	scopeOfDestination,
} from "@/lib/destination";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { claimSocket, type SocketScope } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";

/** The `?redirect=` a sign-in address carries, once it has been checked. */
export type RedirectSearch = { redirect?: Destination };

/**
 * `?redirect=` as every sign-in route accepts it: a destination inside this
 * ELASO Instance ({@link parseDestination}), and `undefined` otherwise, which
 * reads as `/app`.
 *
 * The key is always returned, `undefined` included. The router spreads each
 * route's validated search over its parent's, and the root route keeps the
 * address's raw search, so a key left out lets the raw value through to the
 * page. `undefined` is left out of links built from it, so an address nobody
 * put a redirect in does not grow one.
 */
export const validateRedirectSearch = (
	search: Record<string, unknown>
): RedirectSearch => ({ redirect: parseDestination(search.redirect) });

/**
 * What a signed-out visitor is offered: the form, titled with the identity
 * home's name; the line that explains why there is no form; or, at the bare
 * host, one field to find their Organisation's address
 * (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md).
 */
export type SignedOut =
	| {
			form: {
				title: string;
				description: string;
				/** The email to start from, when the visitor just told us it. */
				email?: string;
			};
	  }
	| { message: string }
	| { findOrganisation: true };

/** What an address naming no identity home says. */
export const NO_IDENTITY_HOME_MESSAGE =
	"This address names no Organisation. Check the address your Organisation gave you.";

/** What either sign-in address says when the server could not be asked. */
export const SIGN_IN_UNAVAILABLE_MESSAGE = "Sign-in is unavailable right now";

/** Distinguishes, in Sentry, where a failed sign-in was heading. */
const SCOPE_SENTRY_CONTEXT = {
	app: "auth_login",
	operations: "ops_auth_login",
	organization: "org_auth_login",
} as const;

/** The welcome card's button, which says where it goes, by the destination's client. */
const CONTINUE_LABEL: Record<string, string> = {
	"/operations": "Go to Operations",
	"/organization": "Go to Organisation",
	"/instance": "Go to Instance admin",
	"/invite": "Open the invite",
};

const continueLabelOf = (destination: Destination | undefined): string =>
	CONTINUE_LABEL[`/${destination?.split("/")[1] ?? ""}`] ?? "Go to App";

type SignInPageProps = {
	/** Where this address sends the user once signed in; the Learner Client when absent. */
	destination?: Destination;
	signedOut: SignedOut;
};

/**
 * One sign-in address, wherever it sends the user afterwards (TECH-3350). Every
 * destination shares the one sign-in
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md).
 *
 * Everything here was `routes/index.tsx` before, where there was one address and
 * therefore no reason to name it: claiming the socket, sending a signed-in user
 * on, the welcome card. It moved so that the identity
 * home's address (`routes/$label.tsx`) and the bare host (`routes/index.tsx`)
 * behave identically once their one difference — what a signed-out visitor is
 * offered — has been decided by whoever rendered this.
 *
 * It knows nothing about identity homes, addresses or tenants. The tenant is set
 * before this is rendered, in `lib/firebase.ts`, so the form below cannot be
 * submitted against a sign-in that has not been pointed at a pool.
 */
export function SignInPage({ destination, signedOut }: SignInPageProps) {
	const navigate = useNavigate();
	const user = useStore(authStore, (state) => state.user);
	const login = useStore(authStore, (state) => state.login);
	const logout = useStore(authStore, (state) => state.logout);
	const scope = scopeOfDestination(destination);
	const href = destination ?? "/app";

	// The socket loads for where this address sends the user, so the connection a
	// sign-in opens here is the one the destination keeps.
	useEffect(() => {
		claimSocket(scope).catch((error) => {
			console.error(`Failed to claim the socket for ${scope}:`, error);
		});
	}, [scope]);

	// Signed in, the user goes where they were heading: the client they opened,
	// the invite they opened, or the client an email was for.
	useEffect(() => {
		if (user) {
			navigate({ href });
		}
	}, [user, navigate, href]);

	const handleLogout = async () => {
		try {
			await logout();
			toast.success("Logged out successfully!");
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "auth_logout" } });
			const errorMessage =
				error instanceof Error ? error.message : "Logout failed";
			toast.error(errorMessage);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="w-full max-w-md">
				{user ? (
					<Card>
						<CardHeader>
							<CardTitle>Welcome back!</CardTitle>
							<CardDescription>
								You are logged in as <strong>{user.email}</strong>
							</CardDescription>
						</CardHeader>
						<CardFooter className="flex gap-2">
							<Button
								className="flex-1"
								onClick={() => navigate({ href })}
								type="button"
							>
								{continueLabelOf(destination)}
							</Button>
							<Button onClick={handleLogout} type="button" variant="outline">
								Logout
							</Button>
						</CardFooter>
					</Card>
				) : (
					renderSignedOut(signedOut, scope, login, destination)
				)}
			</div>
		</div>
	);
}

const renderSignedOut = (
	signedOut: SignedOut,
	scope: SocketScope,
	login: (email: string, password: string) => Promise<void>,
	destination: Destination | undefined
) => {
	if ("findOrganisation" in signedOut) {
		// Keyed, so a new destination starts the page over: an Instance Manager
		// lookup still pending for the old one cannot leave Continue disabled.
		return <FindOrganisation destination={destination} key={destination} />;
	}
	if ("message" in signedOut) {
		// No form at all, which is the point: an address that names no identity
		// home has no pool to sign anybody into, and offering fields that could
		// only ever be refused would read as "your password is wrong".
		return (
			<p className="text-center text-muted-foreground">{signedOut.message}</p>
		);
	}
	return (
		<LoginCard
			description={signedOut.form.description}
			initialEmail={signedOut.form.email}
			onSubmit={login}
			sentryContext={SCOPE_SENTRY_CONTEXT[scope]}
			title={signedOut.form.title}
		/>
	);
};

const NOT_KNOWN_MESSAGE =
	"This Organisation is not known. Check the address your Organisation gave you.";

type Finding =
	| { kind: "idle" }
	| { kind: "asking" }
	| { kind: "refused"; reason: "not_found" | "unavailable" };

/**
 * The label typed here last, kept in this browser alone so a returning user
 * only presses Continue. What was typed, never read out of an address: where a
 * label sits in one is the server's to decide (TECH-3364).
 */
const LAST_LABEL_KEY = "elaso.lastOrganisationLabel";

const lastLabel = (): string => {
	try {
		return localStorage.getItem(LAST_LABEL_KEY) ?? "";
	} catch {
		return "";
	}
};

const rememberLabel = (label: string): void => {
	try {
		localStorage.setItem(LAST_LABEL_KEY, label);
	} catch {
		// Storage can be unavailable (a private window, blocked site data); the
		// field is simply empty next time.
	}
};

/** A full navigation to a sign-in address, carrying where the user was heading. */
const signInAt = (address: string, destination: Destination | undefined) => {
	const signIn = new URL(address);
	if (destination) signIn.searchParams.set("redirect", destination);
	window.location.assign(signIn.toString());
};

/**
 * The find-your-Organisation page (TECH-3353): one field for the label, sent to
 * the server as typed, and a full navigation to the address it answers. Full,
 * because that address belongs to the server and may not be on this host once
 * TECH-3364 moves the label in front of it. Where the user was heading rides
 * along as the sign-in page's own `?redirect=`.
 *
 * Heading for the ELASO Instance Admin Client, nobody has a label to type: only
 * the Instance Manager's people can use it, so the page goes straight on to the
 * Instance Manager's sign-in (TECH-3474), and offers the field only when that
 * address cannot be had.
 */
function FindOrganisation({
	destination,
}: {
	destination: Destination | undefined;
}) {
	const forInstance =
		destination !== undefined && clientOf(destination) === "/instance";
	const [label, setLabel] = useState(lastLabel);
	const [finding, setFinding] = useState<Finding>(
		forInstance ? { kind: "asking" } : { kind: "idle" }
	);

	useEffect(() => {
		if (!forInstance) return;
		let current = true;
		findInstanceSignInAddress().then((found) => {
			if (!current) return;
			if (found.ok) signInAt(found.address, destination);
			else setFinding({ kind: "idle" });
		});
		return () => {
			current = false;
		};
	}, [forInstance, destination]);

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		const typed = label.trim();
		if (!typed) {
			return;
		}
		setFinding({ kind: "asking" });
		const found = await findSignInAddress(typed);
		if (found.ok) {
			rememberLabel(typed);
			signInAt(found.address, destination);
			return;
		}
		setFinding({ kind: "refused", reason: found.reason });
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>Find your Organisation</CardTitle>
				<CardDescription>
					Enter the name your Organisation uses in its ELASO address.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<form className="space-y-4" onSubmit={handleSubmit}>
					<div className="space-y-2">
						<Label htmlFor="organisation-label">Organisation</Label>
						<Input
							autoCapitalize="none"
							autoComplete="organization"
							id="organisation-label"
							onChange={(event) => setLabel(event.target.value)}
							placeholder="your-organisation"
							required
							spellCheck={false}
							value={label}
						/>
					</div>
					{finding.kind === "refused" ? (
						<p className="text-destructive text-sm">
							{finding.reason === "not_found"
								? NOT_KNOWN_MESSAGE
								: SIGN_IN_UNAVAILABLE_MESSAGE}
						</p>
					) : null}
					<Button
						className="w-full"
						disabled={finding.kind === "asking"}
						type="submit"
					>
						{finding.kind === "asking" ? "Finding…" : "Continue"}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
}
